import React, { useEffect, useState } from "react";
import axios from "axios";
import { Link, useNavigate } from "react-router-dom";
import Header from "../components/layout/Header";
import Footer from "../components/layout/Footer";
import { 
  Edit3, 
  Trash2, 
  Eye, 
  Plus, 
  Package, 
  TrendingUp, 
  Clock,
  MessageCircle,
  AlertCircle,
  ShieldAlert
} from "lucide-react";
import { useModal } from "../context/ModalContext";

const apiUrl = process.env.REACT_APP_API_URL;

export default function MyPostsPage() {
  const { showAlert, showConfirm } = useModal();
  const navigate = useNavigate();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  const uStr = localStorage.getItem("user") || sessionStorage.getItem("user");
  const user = uStr ? JSON.parse(uStr) : null;

  useEffect(() => {
    if (!user?.id) return;

    axios
      .get(`${apiUrl}/api/my-posts`, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("token") || sessionStorage.getItem("token")}`,
        },
      })
      .then((res) => {
        setPosts(res.data);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, []);

  const handleMessageAdmin = async (postId) => {
    try {
      // Initiate conversation with Admin (ID: 1)
      await axios.post(`${apiUrl}/api/conversations`, { 
        postId, 
        sellerId: 1 
      }, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token") || sessionStorage.getItem("token")}`,
        },
      });
      navigate("/messages");
    } catch (err) {
      showAlert("Failed to start conversation with admin.", "error");
    }
  };

  const handleDelete = async (postId) => {
    showConfirm("Are you sure you want to delete this post? This action cannot be undone.", async () => {
      try {
        await axios.delete(`${apiUrl}/api/posts/${postId}`, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token") || sessionStorage.getItem("token")}`,
          }
        });
        setPosts((prev) => prev.filter((x) => x.id !== postId));
      } catch (err) {
        showAlert("Failed to delete post.", "error");
      }
    });
  };

  const totalViews = posts.reduce((acc, p) => acc + (p.views || 0), 0);

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <Header />

      <main className="flex-1 max-w-7xl mx-auto w-full px-6 py-12">
        {/* Page Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 mb-10">
          <div>
            <h1 className="text-4xl font-black text-gray-900 tracking-tight mb-2">My Posts</h1>
            <p className="text-gray-500 font-medium">Manage and track your listed items</p>
          </div>
          <Link
            to="/PostItemPage"
            className="flex items-center justify-center gap-2 px-6 py-3 bg-green-600 text-white font-bold rounded-2xl shadow-lg shadow-green-100 hover:bg-green-700 transition transform hover:scale-105"
          >
            <Plus className="w-5 h-5" />
            Post New Item
          </Link>
        </div>

        {/* Stats Summary */}
        {!loading && posts.length > 0 && (
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 mb-12">
            <div className="bg-white p-6 rounded-[2rem] border border-gray-100 shadow-sm flex items-center gap-5">
              <div className="w-14 h-14 bg-blue-50 text-blue-600 rounded-2xl flex items-center justify-center">
                <Package className="w-7 h-7" />
              </div>
              <div>
                <p className="text-xs font-bold text-gray-400 uppercase tracking-widest">Total Items</p>
                <p className="text-2xl font-black text-gray-900">{posts.length}</p>
              </div>
            </div>
            <div className="bg-white p-6 rounded-[2rem] border border-gray-100 shadow-sm flex items-center gap-5">
              <div className="w-14 h-14 bg-green-50 text-green-600 rounded-2xl flex items-center justify-center">
                <TrendingUp className="w-7 h-7" />
              </div>
              <div>
                <p className="text-xs font-bold text-gray-400 uppercase tracking-widest">Total Views</p>
                <p className="text-2xl font-black text-gray-900">{totalViews}</p>
              </div>
            </div>
            <div className="bg-white p-6 rounded-[2rem] border border-gray-100 shadow-sm flex items-center gap-5">
              <div className="w-14 h-14 bg-purple-50 text-purple-600 rounded-2xl flex items-center justify-center">
                <Clock className="w-7 h-7" />
              </div>
              <div>
                <p className="text-xs font-bold text-gray-400 uppercase tracking-widest">Active Listings</p>
                <p className="text-2xl font-black text-gray-900">{posts.filter(p => p.is_hidden === 0).length}</p>
              </div>
            </div>
          </div>
        )}

        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="bg-white rounded-3xl h-96 animate-pulse border border-gray-100"></div>
            ))}
          </div>
        ) : posts.length === 0 ? (
          <div className="bg-white rounded-[3rem] p-20 text-center border border-dashed border-gray-200">
            <div className="w-24 h-24 bg-gray-50 text-gray-300 rounded-full flex items-center justify-center mx-auto mb-6">
              <AlertCircle className="w-12 h-12" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">No active posts</h2>
            <p className="text-gray-500 mb-8 max-w-sm mx-auto">You haven't listed any items for sale yet. Start selling today!</p>
            <Link
              to="/PostItemPage"
              className="inline-flex items-center gap-2 px-8 py-4 bg-green-600 text-white font-bold rounded-2xl shadow-lg shadow-green-100 hover:bg-green-700 transition"
            >
              <Plus className="w-5 h-5" />
              Create Your First Post
            </Link>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {posts.map((p) => (
              <div
                key={p.id}
                className={`group bg-white rounded-[2.5rem] border border-gray-100 shadow-sm hover:shadow-2xl hover:-translate-y-2 transition-all duration-300 overflow-hidden ${p.is_hidden ? 'opacity-75 grayscale-[0.5]' : ''}`}
              >
                {/* Image Container */}
                <div className="relative aspect-[4/5] overflow-hidden bg-gray-100">
                  <img
                    src={p.image}
                    alt={p.title}
                    className={`w-full h-full object-cover transition duration-500 group-hover:scale-110 ${p.is_hidden ? 'brightness-75' : ''}`}
                  />
                  <div className="absolute top-4 left-4 flex flex-col gap-2">
                    <span className="px-3 py-1 bg-white/90 backdrop-blur-md text-[10px] font-black uppercase tracking-widest text-gray-700 rounded-lg shadow-sm w-fit">
                      {p.condition}
                    </span>
                    {p.is_hidden === 1 && (
                      <span className="px-3 py-1 bg-red-600 text-white text-[10px] font-black uppercase tracking-widest rounded-lg shadow-lg flex items-center gap-1 animate-pulse">
                        <ShieldAlert size={12} />
                        Hidden by Admin
                      </span>
                    )}
                  </div>
                  <div className="absolute top-4 right-4 flex items-center gap-1.5 px-3 py-1 bg-black/50 backdrop-blur-md text-white text-[10px] font-bold rounded-lg shadow-sm">
                    <Eye className="w-3.5 h-3.5" />
                    {p.views || 0}
                  </div>
                </div>

                {/* Content */}
                <div className="p-6">
                  <div className="mb-4">
                    <h3 className="font-bold text-gray-900 text-lg leading-tight mb-1 line-clamp-1">
                      {p.title}
                    </h3>
                    <p className="text-xs text-gray-400 font-medium uppercase tracking-wider">
                      {p.brand} {p.model}
                    </p>
                  </div>

                  <div className="flex items-center justify-between mb-6">
                    <p className="text-xl font-black text-green-600">
                      {Number(p.price)?.toLocaleString("en-US")}
                      <span className="text-[10px] ml-1 text-gray-400">ETB</span>
                    </p>
                  </div>

                  {/* Actions */}
                  <div className="grid grid-cols-2 gap-3 pt-4 border-t border-gray-50">
                    {p.is_hidden === 1 ? (
                      <button
                        onClick={() => handleMessageAdmin(p.id)}
                        className="flex items-center justify-center gap-2 py-3 px-4 bg-blue-50 text-blue-600 font-bold text-sm rounded-xl hover:bg-blue-100 transition"
                      >
                        <MessageCircle className="w-4 h-4" />
                        Message Admin
                      </button>
                    ) : (
                      <Link
                        to={`/edit-post/${p.id}`}
                        className="flex items-center justify-center gap-2 py-3 px-4 bg-gray-50 text-gray-700 font-bold text-sm rounded-xl hover:bg-blue-50 hover:text-blue-600 transition"
                      >
                        <Edit3 className="w-4 h-4" />
                        Edit
                      </Link>
                    )}
                    {!p.is_hidden && (
                      <button
                        onClick={() => handleDelete(p.id)}
                        className="flex items-center justify-center gap-2 py-3 px-4 bg-red-50 text-red-600 font-bold text-sm rounded-xl hover:bg-red-100 transition"
                      >
                        <Trash2 className="w-4 h-4" />
                        Delete
                      </button>
                    )}
                  </div>
                  
                  {p.is_hidden === 1 ? (
                    <div className="mt-4 p-4 bg-red-50 rounded-2xl border border-red-100">
                      <p className="text-[10px] font-bold text-red-600 uppercase mb-1">Moderation Notice</p>
                      <p className="text-xs text-red-700 leading-tight">
                        This post has been hidden by an administrator. Please contact support or use the messaging system to clarify.
                      </p>
                    </div>
                  ) : (
                    <Link 
                      to={`/detail/${p.id}`}
                      className="mt-3 w-full flex items-center justify-center gap-2 py-2 text-xs font-bold text-gray-400 hover:text-green-600 transition"
                    >
                      View Listing Details →
                    </Link>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </main>

      <Footer />
    </div>
  );
}
